import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingRow, TextInput, Section } from "./SettingsControls";
import MultiPhoneInput from "src/components/PhoneInput/MultiPhoneInput";
import { SpecialtyPicker } from "./SpecialtyPicker";
import { normalizePlz } from "src/helpers/sanitizeInput";

type PraxisFields = {
    name: string;
    specialization: string[];
    street: string;
    plz: string;
    city: string;
    email: string;
    tel: string[];
    logo: string;
};

interface PraxisSettingsProps {
    praxis: PraxisFields;
    saved: boolean;
    dirty?: boolean;
    onChange: (p: PraxisFields) => void;
    onSave: () => void;
}

const MAX_IMG_BYTES = 5 * 1024 * 1024;

export function PraxisSettings({ praxis, saved, dirty, onChange, onSave }: PraxisSettingsProps) {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [logoError, setLogoError] = useState<string | null>(null);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > MAX_IMG_BYTES) {
            setLogoError(t("form.imageTooLarge"));
            return;
        }
        setLogoError(null);
        const reader = new FileReader();
        reader.onload = () => onChange({ ...praxis, logo: reader.result as string });
        reader.readAsDataURL(file);
    };

    return (
        <Section title={t("settings.praxisDetails")}>
            <SettingRow title={t("form.logo")}>
                <div className="avatar-upload">
                    <div className="avatar-upload__preview" onClick={() => fileInputRef.current?.click()}>
                        {praxis.logo
                            ? <img src={praxis.logo} alt="logo" />
                            : <span>{praxis.name?.charAt(0) ?? "P"}</span>
                        }
                        <div className="avatar-upload__overlay">{t("form.change")}</div>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleLogoChange}
                    />
                    {logoError && <p className="avatar-upload__error">{logoError}</p>}
                    {praxis.logo && (
                        <button className="avatar-upload__remove" onClick={() => onChange({ ...praxis, logo: "" })}>
                            {t("form.remove")}
                        </button>
                    )}
                </div>
            </SettingRow>
            <SettingRow title={<>{t("settings.praxis")} <span className="setting-required">*</span></>}>
                <TextInput label={t("settings.praxis")} value={praxis.name} onChange={(v) => onChange({ ...praxis, name: v })} />
            </SettingRow>
            <SettingRow title={<>{t("settings.specialty")} <span className="setting-required">*</span></>} className="setting-row--top">
                <SpecialtyPicker
                    value={praxis.specialization}
                    onChange={(v) => onChange({ ...praxis, specialization: v })}
                />
            </SettingRow>
            <SettingRow title={<>{t("form.streetAndNumber")} <span className="setting-required">*</span></>}>
                <TextInput
                    label={t("form.streetAndNumber")}
                    value={praxis.street}
                    placeholder="Hauptstraße 22a"
                    onChange={(v) => onChange({ ...praxis, street: v })}
                />
            </SettingRow>
            <SettingRow title={<>{t("form.plz")} <span className="setting-required">*</span></>}>
                <TextInput label={t("form.plz")} value={praxis.plz} onChange={(v) => onChange({ ...praxis, plz: normalizePlz(v) })} />
            </SettingRow>
            <SettingRow title={<>{t("form.city")} <span className="setting-required">*</span></>}>
                <TextInput label={t("form.city")} value={praxis.city} onChange={(v) => onChange({ ...praxis, city: v })} />
            </SettingRow>
            <SettingRow title={<>{t("form.email")} <span className="setting-required">*</span></>}>
                <TextInput label={t("form.email")} value={praxis.email} onChange={(v) => onChange({ ...praxis, email: v })} />
            </SettingRow>
            <SettingRow title={<>{t("form.phone")} <span className="setting-required">*</span></>}>
                <MultiPhoneInput value={praxis.tel ?? []} onChange={(v) => onChange({ ...praxis, tel: v })} />
            </SettingRow>
        </Section>
    );
}
